/**
 * The creator's post turnaround — days from the in-store visit to when they post.
 *
 * Deliberately NOT stored. It used to live on the collab as `chosenPostTurnaroundDays` alongside
 * `scheduledPostingDate`, both written from the same expression, so the number was always exactly
 * the gap between the two dates. Storing it bought nothing and cost correctness: any write path
 * that moved the visit without also rewriting the pair left a stale number that still read as
 * plausible. Derive it instead, so a corrupt pair is detectable rather than believable.
 */

/** Brand's post-deadline window when none is configured. In-store only. */
export const DEFAULT_POST_TURNAROUND_DAYS = 7;

/**
 * Whole days between a visit and its posting date, or null when it can't be trusted:
 * either date missing (a collab scheduled before posting dates were written), or a gap that
 * is non-positive or beyond `maxDays` — which means the pair went out of sync and the caller
 * should fall back to a default rather than surface a wrong number.
 *
 * Exact for pairs we write: the posting date is the visit date advanced by whole UTC days, so
 * the time-of-day cancels.
 */
export const deriveTurnaroundDays = (
  visit: Date | null | undefined,
  posting: Date | null | undefined,
  maxDays: number = DEFAULT_POST_TURNAROUND_DAYS
): number | null => {
  if (!visit || !posting) return null;
  const visitMs = visit.getTime();
  const postingMs = posting.getTime();
  if (Number.isNaN(visitMs) || Number.isNaN(postingMs)) return null;

  const days = Math.round((postingMs - visitMs) / 86_400_000);
  return days > 0 && days <= maxDays ? days : null;
};

/** The posting date for a visit and turnaround: whole UTC days, so time-of-day is preserved. */
export const addTurnaroundDays = (visit: Date, turnaroundDays: number): Date => {
  const posting = new Date(visit);
  posting.setUTCDate(posting.getUTCDate() + turnaroundDays);
  return posting;
};
